import React from 'react';
import { Auth } from 'aws-amplify';
import { Drawer } from '@mui/material';
import { usePopoverHandlers } from 'shared/components/user-popover';
import { AuthenticatedElement } from 'ogp/components/common/authenticated-element';
import { useCompanyRelativePath } from 'ogp/hooks';
import { SiteInfo } from 'shared/components';
import { useToggle } from 'shared/hooks';
import { UserPopoverOgp } from 'shared/components/user-popover/user-popover';
import { useQueryClient } from 'react-query';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider/feature-flag-provider';
import { useGetCompanyDetailAndJobGroups } from 'ogp/hooks/queryHooks/companies/use-get-company-detail-and-job-groups';
import { invariant } from 'shared/utils/utils';
import { StyledSidebar } from 'shared/components/sidebar/styled-sidebar';
import { SidebarSectionTitle } from 'shared/components/sidebar/sidebar-section-title';
import { SidebarSection } from 'shared/components/sidebar/sidebar-section';
import { SidebarSectionList } from 'shared/components/sidebar/sidebar-section-list';
import { SidebarSectionLink } from 'shared/components/sidebar/sidebar-section-link';
import { SidebarProfileSection } from 'shared/components/sidebar/sidebar-profile-section';
import { SidebarToggle } from 'shared/components/sidebar/sidebar-toggle';
import { SiteInfoContainer } from 'shared/components/sidebar/site-info-container';
import { useSidebarProvider } from 'shared/contexts/sidebar-provider';
import { useUserRoles } from './use-user-roles';
import { useSidebarItems } from './sidebar-items';
import { CompanyName } from './sidebar-company-name';
import { filterOutSidebarItems } from './filter-out-sidebar-items';
import { SidebarLogo } from './sidebar-logo';

export const Sidebar = () => {
  const { isSidebarOpen, handleClose, handleOpen } = useSidebarProvider();
  const [userLabel, setUserLabel] = React.useState('');
  const { allowAvailability, allowWorkflows, allowCollegialLending } = useFeatureFlagContext();
  const { id: popoverId, anchorEl } = usePopoverHandlers();
  const userRoles = useUserRoles();
  const companyQuery = useGetCompanyDetailAndJobGroups();
  const generatePath = useCompanyRelativePath();

  const { on: userInfoOpen, toggle: toggleUserInfo } = useToggle();
  const queryClient = useQueryClient();

  const logout = async () => {
    await Auth.signOut();
    queryClient.removeQueries();
  };

  const sidebarItems = useSidebarItems();

  // TODO this might be moved to popover itself?
  React.useEffect(() => {
    let isMounted = true;
    Auth.currentUserInfo().then((res) => {
      if (isMounted && res) {
        setUserLabel(res.attributes?.email ?? '');
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  if (companyQuery.isLoading) {
    return null;
  }

  const items = filterOutSidebarItems({
    sidebarItems,
    company: companyQuery.data,
    allowAvailability,
    allowWorkflows,
    allowCollegialLending,
    companyRoles: userRoles?.companyRoles,
  });

  const getSectionPermissions = (section: (typeof items)[number]['key']) => {
    const group = items.find((i) => i.key === section);
    invariant(group);

    return {
      roles: group.children.map((child) => child.roles).flat(),
      policyGroup: group.children[0]?.policyGroup, // TODO do we have a better way to hide sections? https://levelworks.atlassian.net/browse/LEV-775
    };
  };

  return (
    <>
      <StyledSidebar variant="permanent" folded={!isSidebarOpen}>
        <SidebarLogo to={generatePath(items[0].children[0])} isSidebarOpen={isSidebarOpen} />
        <CompanyName />
        {items.map((sidebarItem) => {
          const permissions = getSectionPermissions(sidebarItem.key);

          return (
            <AuthenticatedElement {...permissions} key={sidebarItem.key}>
              <SidebarSection>
                <SidebarSectionTitle isSidebarOpen={isSidebarOpen}>
                  {sidebarItem.name}
                </SidebarSectionTitle>
                <SidebarSectionList>
                  {sidebarItem.children.map((child) => {
                    const path = generatePath(child);
                    return (
                      <AuthenticatedElement
                        key={child.label}
                        roles={child.roles}
                        policyGroup={child.policyGroup}
                      >
                        <SidebarSectionLink {...child} path={path} isSidebarOpen={isSidebarOpen} />
                      </AuthenticatedElement>
                    );
                  })}
                </SidebarSectionList>
              </SidebarSection>
            </AuthenticatedElement>
          );
        })}
        <SidebarProfileSection
          isSidebarOpen={isSidebarOpen}
          handleToggleUserInfo={toggleUserInfo}
          handleLogout={logout}
        />
        <SiteInfoContainer isSidebarOpen={isSidebarOpen}>
          <SiteInfo />
        </SiteInfoContainer>
      </StyledSidebar>
      <SidebarToggle
        isSidebarOpen={isSidebarOpen}
        handleClose={handleClose}
        handleOpen={handleOpen}
      />
      <Drawer anchor="right" open={userInfoOpen} onClose={toggleUserInfo}>
        <UserPopoverOgp
          userLabel={userLabel}
          userRoles={userRoles}
          id={popoverId}
          open={!!anchorEl}
          anchorEl={anchorEl}
        />
      </Drawer>
    </>
  );
};
